/**
 * AddDragContext — custom pointer-based drag-to-add for section tiles.
 *
 * A palette tile calls `start(info, e)` on pointerdown; the provider
 * captures the pointer, tracks the drag on window listeners, and on drop
 * over a page grid inserts the section. State is exposed so PageNode can
 * highlight the grid under the pointer (`overGridId`) and the ghost can
 * follow `pointer`.
 *
 * Pointer-based (not HTML5 DnD) so it works on touch and matches the grid
 * engine's interaction model. Pointer-capture on the tile both enables
 * touch tracking and keeps xyflow from panning mid-drag;
 * `document.elementFromPoint` hit-testing is unaffected by capture.
 *
 * Mirrors the EditorContext/ReadOnlyContext pattern so PageNode can read
 * it across xyflow's node-renderer boundary.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useGridStore, type GridItemConstraints } from "@fredericrous/grid";
import { gapOf, landingFromRect } from "@fredericrous/grid/engine";
import type { UpdateDragTarget } from "@fredericrous/grid/store";

import {
  prepareInlineExternalAdd,
  prepareSectionExternalAdd,
} from "./sectionInsert.js";
import { crossTabTargetAt } from "./useCrossTabDrop.js";
import { isInFlowCanvas } from "./ui/FlowCanvasFrame.js";
import { markDropped } from "./widgets/dropReveal.js";
import { bindAssetToMediaItem } from "./assets/mediaCatalog.js";
import { allocateFieldName } from "./fieldNames.js";
import { patchContentProps } from "./pendingEdits.js";
import { openSettings } from "./settingsTarget.js";
import { openQuickEdit } from "./quickEditTarget.js";
import { registeredWidgetQuickEdit } from "./widgets/catalogRegistry.js";
import { OPEN_SETTINGS_ON_DROP } from "./widgetPresets.js";
import { EDITOR_ROW_HEIGHT_PX } from "@website-builder/schemas";
import { mediaBindAt } from "./widgets/mediaBindRegistry.js";

/** Resolve an EMPTY media widget under a client point that can accept a dragged
 *  asset of `kind` — the drop-to-fill target. Empty widgets register their root
 *  in the media-bind registry (registerMediaBind) ONLY when they have no asset
 *  (`!sha256 && !url`), so a filled widget whose src is merely resolving is
 *  never a target. Null when not over a matching empty widget. */
function mediaBindTargetAt(
  clientX: number,
  clientY: number,
  kind: string,
): { gridId: string; itemId: string } | null {
  const bind = mediaBindAt(document.elementFromPoint(clientX, clientY));
  if (!bind || bind.kind !== kind) return null;
  return { gridId: bind.gridId, itemId: bind.itemId };
}

const MEDIA_WIDGET_KINDS = new Set(["image", "video", "audio"]);

/** Resolve the page-BODY grid + engine landing cell for a SECTION dropped at a
 *  client point. Unlike a widget (crossTabTargetAt), a section always lands in
 *  a page body — and the body is usually covered by existing sections, so a
 *  gridUnderPoint hit-test would land on a section's nested grid (which refuses
 *  containers). Resolve the body via its `[data-page-grid]` wrapper (an ancestor
 *  that spans the whole node) exactly like the old custom path, then compute the
 *  landing the same way the cross-grid engine does, so a section drag previews
 *  and stacks identically to a section MOVE. Null when not over a page body. */
function sectionTargetAt(
  clientX: number,
  clientY: number,
  w: number,
  h: number,
): UpdateDragTarget | null {
  const el = (
    document.elementFromPoint(clientX, clientY) as Element | null
  )?.closest("[data-page-grid]") as HTMLElement | null;
  const gid = el?.getAttribute("data-page-grid") ?? null;
  if (!el || !gid) return null;
  const grid = useGridStore.getState().grids[gid];
  if (!grid) return null;
  const cfg = grid.config;
  const rowH =
    typeof cfg.rowHeight === "number" ? cfg.rowHeight : EDITOR_ROW_HEIGHT_PX;
  const rect = el.getBoundingClientRect();
  // The body is xyflow-scaled by the canvas zoom; derive it from rendered-vs-
  // layout width so the screen pointer maps to grid-local px (its offsetWidth
  // also stands in for a stale containerWidth at far zoom). This derivation
  // STAYS post grid-0.9.4: it measures the `[data-page-grid]` WRAPPER (a
  // custom resolve — sections cover the body, so gridUnderPoint would hit a
  // nested grid), not a scale-aware ResolvedGrid.
  const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
  const landing = landingFromRect(
    (clientX - rect.left) / scale,
    (clientY - rect.top) / scale,
    rect.width / scale,
    cfg.columns,
    rowH,
    gapOf(cfg),
    { w, h },
  );
  return { gridId: gid, landing };
}

// Default cell size a NEW leaf widget is born at, unless its tile's preset says:
// image tall, text medium, spacer a single thin row. Full-width (w:12); the
// author resizes after. Feeds the external-add spec + the landing refit.
const WIDGET_SIZE: Record<string, { w: number; h: number }> = {
  image: { w: 12, h: 4 },
  video: { w: 12, h: 5 },
  audio: { w: 12, h: 2 }, // a compact player row (toggle + waveform + time)
  text: { w: 12, h: 2 },
  spacer: { w: 12, h: 1 },
  "inline-grid": { w: 6, h: 1 },
};

/**
 * After a widget lands: an input gets a field name unique in the section it
 * landed in (the preset carries none — the destination wasn't known), the
 * new widget is selected, and a kind whose preset is a placeholder nobody
 * keeps opens its settings straight away.
 */
function settleDroppedWidget(itemId: string): void {
  const st = useGridStore.getState();
  const gridId = Object.keys(st.grids).find((g) => st.grids[g]?.items[itemId]);
  if (!gridId) return;
  const item = st.grids[gridId]!.items[itemId]!;
  if (item.contentType === "input")
    patchContentProps(gridId, itemId, {
      name: allocateFieldName(gridId, itemId),
    });
  st.setSelection(gridId, [itemId]);
  // Its card when it has one (attached to the widget), else the Inspector.
  if (OPEN_SETTINGS_ON_DROP.has(item.contentType)) {
    if (registeredWidgetQuickEdit(item.contentType))
      openQuickEdit(gridId, itemId);
    else openSettings(gridId, itemId);
  }
}

export interface DraggingInfo {
  /** "section" → dropped into a page grid; "page" → dropped on the canvas;
   *  "widget" → a leaf image/text tile dropped into a section's nested grid. */
  readonly kind: "section" | "page" | "widget";
  readonly templateId: string;
  readonly icon: string;
  readonly label: string;
  /** For kind "widget": the content type to insert. */
  readonly widgetType?: string;
  /** For kind "widget": the cell size it is born at (a design-system preset);
   *  absent → WIDGET_SIZE by content type. */
  readonly size?: { readonly w: number; readonly h: number };
  /** For kind "widget": its grid constraints (a design-system preset). */
  readonly constraints?: GridItemConstraints;
  /** For kind "widget": initial contentProps for the new item (e.g. a media
   *  asset's `{sha256, mime, ext}` when dragged from the Media Library). */
  readonly contentProps?: Record<string, unknown>;
  /** A preview image URL for the drag ghost (the asset itself). Absent → the
   *  structural wireframe preview is used instead. */
  readonly previewUrl?: string;
}

/** Implemented by InnerCanvas (inside ReactFlowProvider) and registered with
 *  the provider so a page drop — handled in a window listener outside the
 *  ReactFlow context — can create a node at the drop point. */
export interface CanvasApi {
  /** Create a page at the drop point. When `targetId` is given, the new page is
   *  attached to that existing page: `mode:"child"` (default) nests it UNDER the
   *  target; `mode:"parent"` inserts it ABOVE the target (the new page takes the
   *  target's old parent slot and the target becomes its child). */
  addPageAt: (
    screenX: number,
    screenY: number,
    templateId: string,
    targetId?: string,
    mode?: "child" | "parent",
  ) => void;
  /** Native hit-test: the page node under a screen point with its on-screen
   *  rect (for the drop highlight), or null. Web uses document.elementFromPoint
   *  instead and leaves this undefined. */
  pageAt?: (
    screenX: number,
    screenY: number,
  ) => {
    id: string;
    rect: { x: number; y: number; width: number; height: number };
    /** Current canvas zoom — lets the native adapter map a screen Y to the
     *  page grid's local px for precise section-slot insertion. */
    zoom: number;
  } | null;
}

interface AddDragState {
  readonly dragging: DraggingInfo | null;
  readonly overGridId: string | null;
  /** true while a "page" drag is over the canvas (drop target). */
  readonly overCanvas: boolean;
  /** Id of the existing page node a "page" drag is hovering, or null. Drives
   *  PageNode's nest affordance. */
  readonly overNodeId: string | null;
  /** For a "page" drag over a node: whether dropping would insert the new page
   *  ABOVE it ("parent", pointer in the top zone) or nest it UNDER it ("child").
   *  Null when not over a node. Drives which end the affordance highlights and
   *  which mutation the drop runs. */
  readonly pageNestMode: "parent" | "child" | null;
  readonly pointer: { x: number; y: number } | null;
  /** What initiated the drag — "mouse" | "pen" | "touch" (null when idle).
   *  Drives ghost placement: a finger occludes the target (center the preview
   *  under it), a mouse is a precise point (trail the preview off it so the drop
   *  marker stays visible). Per-interaction, so a mouse vs finger on the SAME
   *  device is classified correctly. */
  readonly pointerType: string | null;
  /** Item id of an EMPTY media widget the pointer is over during a media-asset
   *  drag (drop-to-fill target). The widget rings itself when this === its id. */
  readonly bindTargetId: string | null;
  readonly start: (info: DraggingInfo, e: React.PointerEvent) => void;
  readonly cancel: () => void;
  readonly registerCanvas: (api: CanvasApi | null) => void;
}

const AddDragContext = createContext<AddDragState | null>(null);

/** Safe to call outside the provider (returns an inert state) so PageNode
 *  works on canvases not wrapped by AddDragProvider (e.g. PreviewCanvas). */
const INERT: AddDragState = {
  dragging: null,
  overGridId: null,
  overCanvas: false,
  overNodeId: null,
  pageNestMode: null,
  pointer: null,
  pointerType: null,
  bindTargetId: null,
  start: () => {},
  cancel: () => {},
  registerCanvas: () => {},
};

export function useAddDrag(): AddDragState {
  return useContext(AddDragContext) ?? INERT;
}

const DRAG_THRESHOLD = 5; // px — ADR-003 click-vs-drag hysteresis

// Fixed-size top band (in the target node's OWN layout px, so it's a consistent,
// learnable target at any zoom) where dropping a page tile inserts it as the
// target's PARENT; anywhere below → child. mvp-first uses a clamped ~200px band;
// 150px reads as a clear "top strip" without dominating short pages.
const PAGE_PARENT_ZONE_PX = 150;

interface Session {
  info: DraggingInfo;
  startX: number;
  startY: number;
  active: boolean;
  overGridId: string | null;
  overCanvas: boolean;
  overNodeId: string | null;
  pageNestMode: "parent" | "child" | null;
  /** True once this drag has an OPEN grid-engine external-add session (widget /
   *  section kinds). While open, the engine renders the same landing preview a
   *  cross-grid move does; it must be committed (endExternalAdd) or cancelled
   *  (cancelExternalAdd) exactly once. */
  extAddStarted: boolean;
  /** Targeting meta for the open external add (kind + size), reused each frame
   *  to resolve the grid + landing under the pointer (crossTabTargetAt). */
  extAddMeta: {
    kind: "app" | "container";
    w: number;
    h: number;
    ct: string;
  } | null;
  /** For a SECTION external add: the nested grid seeded up front. Deleted if the
   *  drag is cancelled / dropped outside (else it orphans in the store). */
  extAddSectionGridId?: string;
  /** When the pointer is over an EMPTY media widget of matching kind: bind the
   *  dragged asset INTO it on drop (instead of creating a new widget). */
  bindTarget: { gridId: string; itemId: string } | null;
  teardown: () => void;
}

export function AddDragProvider({ children }: { children: ReactNode }) {
  const [dragging, setDragging] = useState<DraggingInfo | null>(null);
  const [overGridId, setOverGridId] = useState<string | null>(null);
  const [overCanvas, setOverCanvas] = useState(false);
  const [overNodeId, setOverNodeId] = useState<string | null>(null);
  const [pageNestMode, setPageNestMode] = useState<"parent" | "child" | null>(
    null,
  );
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [pointerType, setPointerType] = useState<string | null>(null);
  const [bindTargetId, setBindTargetId] = useState<string | null>(null);
  const session = useRef<Session | null>(null);
  // Registered by InnerCanvas; read at drop time (no-op if the live canvas
  // unmounted mid-session, e.g. PreviewCanvas swapping in).
  const canvasRef = useRef<CanvasApi | null>(null);
  const registerCanvas = useCallback((api: CanvasApi | null) => {
    canvasRef.current = api;
  }, []);

  const start = useCallback((info: DraggingInfo, e: React.PointerEvent) => {
    if (session.current) return;
    const startX = e.clientX;
    const startY = e.clientY;
    // Classify by the pointer that started THIS drag (empty on rare legacy
    // engines → treat as mouse, the desktop default).
    setPointerType(e.pointerType || "mouse");
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }

    const s: Session = {
      info,
      startX,
      startY,
      active: false,
      overGridId: null,
      overCanvas: false,
      overNodeId: null,
      pageNestMode: null,
      extAddStarted: false,
      extAddMeta: null,
      bindTarget: null,
      teardown: () => {},
    };

    // Close an open external-add session exactly once. `commit` runs the drop
    // (endExternalAdd → new item id); otherwise it cancels (dropped outside /
    // Escape / teardown). Idempotent so teardown can safely call it after a
    // commit already closed the session. Cleans up a SECTION's up-front nested
    // grid whenever the section wasn't actually committed (cancel, or a commit
    // that landed nowhere) so no seeded grid orphans in the store.
    const endExtAdd = (commit: boolean): string | null => {
      if (!s.extAddStarted) return null;
      s.extAddStarted = false;
      const st = useGridStore.getState();
      const orphan = s.extAddSectionGridId;
      s.extAddSectionGridId = undefined;
      if (!commit) {
        st.cancelExternalAdd();
        if (orphan) st.deleteGrid(orphan);
        return null;
      }
      const id = st.endExternalAdd();
      if (id === null && orphan) st.deleteGrid(orphan);
      return id;
    };

    const onMove = (ev: PointerEvent) => {
      if (!s.active) {
        if (
          Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD
        )
          return;
        s.active = true;
        setDragging(s.info);
      }
      setPointer({ x: ev.clientX, y: ev.clientY });
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      if (s.info.kind === "page") {
        // Page drag drops onto the canvas — and if it's over an existing page
        // node, the new page attaches to it (xyflow tags each node wrapper with
        // data-id). Otherwise it's a free drop on empty canvas.
        const over = isInFlowCanvas(el);
        const nodeEl = el?.closest(".react-flow__node") as HTMLElement | null;
        const nodeId = nodeEl?.getAttribute("data-id") ?? null;
        // Fixed-size top band → insert the new page ABOVE the target (parent +
        // reorder); anywhere else on it → nest UNDER it (child). The band is
        // measured in the node's own LAYOUT px (zoom-independent) — derive the
        // zoom from rendered-vs-layout height and unscale the pointer offset — so
        // the target is the same size whatever the canvas zoom.
        let mode: "parent" | "child" | null = null;
        if (nodeId && nodeEl) {
          const r = nodeEl.getBoundingClientRect();
          const layoutH = nodeEl.offsetHeight || 1;
          const zoom = r.height > 0 ? r.height / layoutH : 1;
          const localY = (ev.clientY - r.top) / zoom;
          mode = localY < PAGE_PARENT_ZONE_PX ? "parent" : "child";
        }
        s.overCanvas = over;
        s.overNodeId = nodeId;
        s.pageNestMode = mode;
        setOverCanvas(over);
        setOverNodeId(nodeId);
        setPageNestMode(mode);
      } else {
        // SECTION | WIDGET → the grid engine's EXTERNAL-ADD session: the EXACT
        // machinery a cross-grid MOVE uses (hit-test every grid, the blue
        // placeholder + neighbour displacement + grow, the accept gate). So a
        // new item lands anywhere a moved one can — a widget into a section's
        // nested grid OR the shared header/footer bands, a section into a page
        // body — each with the move's landing preview. Replaces the old
        // body-only hit-test + 1-line marker.
        if (!s.extAddStarted) {
          const st = useGridStore.getState();
          if (s.info.kind === "section") {
            // Seed the nested grid up front so the block previews with its
            // content; cancel/drop-outside cleans it up (endExtAdd).
            const prep = prepareSectionExternalAdd(s.info.templateId);
            if (!prep) return;
            const id = st.startExternalAdd(prep.spec);
            if (id === null) {
              st.deleteGrid(prep.sectionGridId); // another interaction is live
              return;
            }
            s.extAddStarted = true;
            s.extAddSectionGridId = prep.sectionGridId;
            s.extAddMeta = {
              kind: "container",
              w: prep.spec.w,
              h: prep.spec.h,
              ct: "grid",
            };
          } else if (s.info.widgetType === "inline-grid") {
            // An Inline arrives EMPTY, with its one-row grid made up front like
            // a section's; it targets like a leaf widget (a section grid or a
            // band), and cancel/drop-outside deletes the grid (endExtAdd).
            const prep = prepareInlineExternalAdd(s.info.size?.w);
            const id = st.startExternalAdd(prep.spec);
            if (id === null) {
              st.deleteGrid(prep.nestedGridId); // another interaction is live
              return;
            }
            s.extAddStarted = true;
            s.extAddSectionGridId = prep.nestedGridId;
            s.extAddMeta = {
              kind: "app",
              w: prep.spec.w,
              h: prep.spec.h,
              ct: "inline-grid",
            };
          } else {
            const wt = s.info.widgetType;
            if (!wt) return;
            const { w, h } = s.info.size ?? WIDGET_SIZE[wt] ?? { w: 12, h: 2 };
            const id = st.startExternalAdd({
              contentType: wt,
              w,
              h,
              contentProps: s.info.contentProps ?? {},
              ...(s.info.constraints
                ? { constraints: s.info.constraints }
                : {}),
            });
            if (id === null) return; // another interaction is live
            s.extAddStarted = true;
            s.extAddMeta = { kind: "app", w, h, ct: wt };
          }
        }
        // Resolve the destination grid + landing under the pointer. WIDGET: the
        // nearest accepting grid (crossTabTargetAt — a widget over a bare body
        // bumps to the closest section; header/footer bands accept it directly).
        // SECTION: always a page body (sectionTargetAt — resolved via the body
        // wrapper so it works even when sections cover the point). A null target
        // parks the preview (over nothing that accepts this kind).
        if (s.extAddMeta) {
          const m = s.extAddMeta;
          // Drop-to-fill: for a MEDIA widget drag, an empty matching-kind widget
          // under the pointer wins over the grid external-add — park the phantom
          // new item and remember the bind target (highlighted + bound on drop).
          const bind =
            m.kind === "app" && MEDIA_WIDGET_KINDS.has(m.ct)
              ? mediaBindTargetAt(ev.clientX, ev.clientY, m.ct)
              : null;
          if (bind) {
            if (s.bindTarget?.itemId !== bind.itemId) {
              s.bindTarget = bind;
              setBindTargetId(bind.itemId);
            }
            useGridStore
              .getState()
              .updateExternalAdd(ev.clientX, ev.clientY, null);
          } else {
            if (s.bindTarget) {
              s.bindTarget = null;
              setBindTargetId(null);
            }
            const target =
              m.kind === "container"
                ? sectionTargetAt(ev.clientX, ev.clientY, m.w, m.h)
                : crossTabTargetAt(ev.clientX, ev.clientY, m);
            useGridStore
              .getState()
              .updateExternalAdd(ev.clientX, ev.clientY, target);
          }
        }
      }
    };
    const teardown = () => {
      // Safety: if an external-add session is still open (Escape, pointercancel,
      // the exposed cancel(), or an unmount), cancel it so no orphaned preview
      // is left. A committed drop already closed it via endExtAdd(true), so this
      // is a no-op there.
      endExtAdd(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancelEv);
      window.removeEventListener("keydown", onKey);
      session.current = null;
      setDragging(null);
      setOverGridId(null);
      setOverCanvas(false);
      setOverNodeId(null);
      setPageNestMode(null);
      setPointer(null);
      setPointerType(null);
      setBindTargetId(null);
    };
    const onUp = (ev: PointerEvent) => {
      const { kind, templateId } = s.info;
      const onCanvas = s.active && s.overCanvas;
      const overNode = s.active ? s.overNodeId : null;
      const nestMode = s.active ? s.pageNestMode : null;
      const x = ev.clientX;
      const y = ev.clientY;
      // DROP-TO-FILL: if the pointer is over an empty media widget, bind the
      // dragged asset INTO it and discard the phantom new item — no new widget.
      const bind = s.active ? s.bindTarget : null;
      if (bind) {
        endExtAdd(false); // cancel the parked external-add
        const cp = s.info.contentProps ?? {};
        const sha = typeof cp["sha256"] === "string" ? cp["sha256"] : undefined;
        if (sha) {
          bindAssetToMediaItem(bind.gridId, bind.itemId, {
            sha256: sha,
            mime: typeof cp["mime"] === "string" ? cp["mime"] : undefined,
            ext: typeof cp["ext"] === "string" ? cp["ext"] : undefined,
          });
        }
        teardown();
        return;
      }
      // COMMIT any open external-add (widget) BEFORE teardown — teardown's
      // safety-cancel then no-ops. endExternalAdd returns the new item id, or
      // null when the drop was outside every accepting grid (nothing added).
      const extNewId = s.active && s.extAddStarted ? endExtAdd(true) : null;
      teardown();
      if (kind === "page") {
        // Over an existing page → attach (top zone = parent+reorder, else child);
        // else a free drop on empty canvas.
        if (overNode)
          canvasRef.current?.addPageAt(
            x,
            y,
            templateId,
            overNode,
            nestMode ?? "child",
          );
        else if (onCanvas) canvasRef.current?.addPageAt(x, y, templateId);
      } else if (extNewId) {
        // WIDGET or SECTION — the engine committed the new item at the previewed
        // landing (widget → header/footer/section grid; section → page body).
        // Fly it in from the drop point.
        markDropped(extNewId, { x, y });
        if (kind === "widget") settleDroppedWidget(extNewId);
      }
    };
    const onCancelEv = () => teardown();
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") teardown();
    };

    s.teardown = teardown;
    session.current = s;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancelEv);
    window.addEventListener("keydown", onKey);
  }, []);

  const cancel = useCallback(() => session.current?.teardown(), []);

  const value = useMemo<AddDragState>(
    () => ({
      dragging,
      overGridId,
      overCanvas,
      overNodeId,
      pageNestMode,
      pointer,
      pointerType,
      bindTargetId,
      start,
      cancel,
      registerCanvas,
    }),
    [
      dragging,
      overGridId,
      overCanvas,
      overNodeId,
      pageNestMode,
      pointer,
      pointerType,
      bindTargetId,
      start,
      cancel,
      registerCanvas,
    ],
  );

  return (
    <AddDragContext.Provider value={value}>{children}</AddDragContext.Provider>
  );
}
